import {
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  Grid,
  Link,
  Radio,
  TextField,
  Typography,
} from "@mui/material";
import { useState } from "react";
import { useSelector } from "react-redux";

const BORROWER_TYPES = {
  INDIVIDUAL: "individual",
  ENTITY: "entity",
};

const HIDE_INDIVIDUAL_BORROWER_OPTION = String(
  process.env.REACT_APP_HIDE_INDIVIDUAL_BORROWER_OPTION ?? "true"
).toLowerCase() === "true";

const ENTITY_OPTIONS = [
  {
    value: "company",
    label: "Company",
    description: "A formal business organization",
  },
  {
    value: "partnership_firm",
    label: "Partnership",
    description: "A business partnership between two or more people",
  },
  {
    value: "propreitorship_firm",
    label: "Proprietorship",
    description: "A business owned and run by a single individual",
  },
];

const BorrowerContent = ({ handleAddProfile }) => {
  const user = useSelector((state) => state.user.user);

  const [borrowerType, setBorrowerType] = useState(
    HIDE_INDIVIDUAL_BORROWER_OPTION ? BORROWER_TYPES.ENTITY : ""
  );
  const [entityName, setEntityName] = useState("");
  const [entityType, setEntityType] = useState("");
  const [termsAccepted, setTermsAccepted] = useState(false);

  const isEntityBorrower = borrowerType === BORROWER_TYPES.ENTITY;
  const showBorrowerTypeSelection = !HIDE_INDIVIDUAL_BORROWER_OPTION;

  const handleBorrowerTypeChange = (e) => {
    setBorrowerType(e.target.value);

    // reset entity fields if switching
    if (e.target.value === BORROWER_TYPES.INDIVIDUAL) {
      setEntityName("");
      setEntityType("");
    }
    setTermsAccepted(false); // reset terms when switching type
  };

  const handleEntityTypeChange = (e) => {
    setEntityType(e.target.value);
    setTermsAccepted(false); // reset on change
  };

  const isFormValid = () => {
    if (!borrowerType) return false;
    if (isEntityBorrower) {
      return entityName.trim() !== "" && entityType !== "" && termsAccepted;
    }
    return termsAccepted;
  };

  const handleSubmit = () => {
    if (!isFormValid()) return;

    const data = {
      userType: "borrower",
      mobileNumber: user?.MobileNumber?.replace(/-/g, ""),
      email: user?.Email,
      id: user?.ID,
      name: user?.Name,
      borrowerType,
      ...(borrowerType === BORROWER_TYPES.ENTITY && {
        entityName: entityName.trim(),
        entityType: entityType.trim(),
      }),
    };

    handleAddProfile(data);
  };

  return (
    <Grid
      container
      justifyContent="center"
      sx={{
        minHeight: "70vh",
        backgroundColor: "#f9f9f9",
        py: { xs: 2, sm: 4 },
        px: { xs: 1, sm: 3 },
      }}
    >
      <Grid
        item
        xs={12}
        sm={8}
        md={6}
        sx={{
          display: "flex",
          flexDirection: "column",
          p: { xs: 2, sm: 4 },
          backgroundColor: "white",
          borderRadius: 2,
          boxShadow: 3,
        }}
      >
        {/* Logo */}
        <Box sx={{ textAlign: "center", mb: 3 }}>
          <img src="./dequiLogo.png" alt="Logo" style={{ width: "150px" }} />
        </Box>

        {/* Borrower Type Selection */}
        {showBorrowerTypeSelection && (
          <>
            <Typography
              sx={{
                fontWeight: 700,
                color: "#004AAD",
                fontSize: { xs: "16px", md: "18px" },
                mb: 1,
              }}
            >
              Type of Borrower
            </Typography>
            <Typography
              sx={{
                fontStyle: "italic",
                mb: 2,
                fontSize: { xs: "11px", md: "12px" },
              }}
            >
              Please select the type of borrower you are:
            </Typography>

            <Box
              sx={{
                display: "flex",
                flexDirection: { xs: "column", sm: "row" },
                gap: 2,
                mb: 3,
              }}
            >
              {[
                {
                  type: BORROWER_TYPES.INDIVIDUAL,
                  title: "1. Individual",
                  description:
                    "An individual seeking a personal or small business loan",
                },
                {
                  type: BORROWER_TYPES.ENTITY,
                  title: "2. Entity",
                  description: "Represent a business entity applying for a loan",
                },
              ].map(({ type, title, description }) => {
                const isSelected = borrowerType === type;
                return (
                  <Box
                    key={type}
                    sx={{
                      border: "1px solid",
                      borderColor: isSelected ? "#004AAD" : "#818181",
                      borderRadius: "8px",
                      p: 2,
                      flex: 1,
                      cursor: "pointer",
                      backgroundColor: isSelected
                        ? "rgba(0, 74, 173, 0.08)"
                        : "transparent",
                      "&:hover": {
                        borderColor: "#004AAD",
                      },
                    }}
                    onClick={() =>
                      handleBorrowerTypeChange({ target: { value: type } })
                    }
                  >
                    <FormControlLabel
                      value={type}
                      control={
                        <Radio style={{ color: "#004AAD" }} checked={isSelected} />
                      }
                      label={
                        <Box>
                          <Typography
                            sx={{ fontWeight: "bold", fontSize: "0.85rem" }}
                          >
                            {title}
                          </Typography>
                          <Typography
                            sx={{ fontSize: "0.75rem", color: "#818181" }}
                          >
                            {description}
                          </Typography>
                        </Box>
                      }
                      sx={{ alignItems: "flex-start" }}
                    />
                  </Box>
                );
              })}
            </Box>
          </>
        )}

        {/* Entity Section */}
        {isEntityBorrower && (
          <>
            <Typography
              sx={{
                fontWeight: 700,
                color: "#004AAD",
                fontSize: { xs: "16px", md: "18px" },
                mb: 1,
              }}
            >
              Entity Details
            </Typography>
            <Typography
              sx={{
                fontStyle: "italic",
                mb: 2,
                fontSize: { xs: "11px", md: "12px" },
              }}
            >
              Please provide your entity details:
            </Typography>

            <TextField
              fullWidth
              label="Entity Name"
              value={entityName}
              onChange={(e) => setEntityName(e.target.value)}
              sx={{ mb: 2 }}
              required
            />

            <Box
              sx={{
                display: "flex",
                flexDirection: { xs: "column", sm: "row" },
                gap: 2,
                mb: 2,
              }}
            >
              {ENTITY_OPTIONS.map(({ value, label, description }) => {
                const isSelected = entityType === value;
                return (
                  <Box
                    key={value}
                    sx={{
                      border: "1px solid",
                      borderColor: isSelected ? "#004AAD" : "#818181",
                      borderRadius: "8px",
                      p: 2,
                      flex: 1,
                      cursor: "pointer",
                      backgroundColor: isSelected
                        ? "rgba(0, 74, 173, 0.08)"
                        : "transparent",
                      "&:hover": {
                        borderColor: "#004AAD",
                      },
                    }}
                    onClick={() =>
                      handleEntityTypeChange({ target: { value } })
                    }
                  >
                    <FormControlLabel
                      value={value}
                      control={
                        <Radio
                          style={{ color: "#004AAD" }}
                          checked={isSelected}
                        />
                      }
                      label={
                        <Box>
                          <Typography
                            sx={{ fontWeight: "bold", fontSize: "0.85rem" }}
                          >
                            {label}
                          </Typography>
                          <Typography
                            sx={{ fontSize: "0.75rem", color: "#818181" }}
                          >
                            {description}
                          </Typography>
                        </Box>
                      }
                      sx={{ alignItems: "flex-start" }}
                    />
                  </Box>
                );
              })}
            </Box>
          </>
        )}

        {/* Declaration Checkbox */}
        {(borrowerType === BORROWER_TYPES.INDIVIDUAL || isEntityBorrower) && (
            <Box
              sx={{
                border: "1px solid #ddd",
                borderRadius: "12px",
                p: 2,
                backgroundColor: "#fafafa",
                mt: 2,
              }}
            >
              <FormControlLabel
                control={
                  <Checkbox
                    checked={termsAccepted}
                    onChange={(e) => setTermsAccepted(e.target.checked)}
                    name="termsAccepted"
                  />
                }
                label={
                  <Typography variant="body2" sx={{ lineHeight: 1.6 }}>
                    <span style={{ fontWeight: 600 }}>I hereby confirm and agree that:</span>
                    <span style={{ color: "red" }}> *</span>
                    <ul style={{ marginTop: 8, paddingLeft: 20 }}>
                      {borrowerType === BORROWER_TYPES.INDIVIDUAL && (
                        <li>
                          <strong>Register as Individual:</strong> If you are applying for a loan in your personal name (as an Individual)
                        </li>
                      )}
                      {borrowerType === BORROWER_TYPES.ENTITY && (
                        <li>
                          <strong>Registering as an Entity:</strong> If you are applying for a loan on behalf of an entity (e.g., a company or business).
                        </li>
                      )}
                      <li>
                        I/We declare that we have a clean and satisfactory credit history, with no history of default or significant delays in the repayment of financial obligations.
                      </li>
                      <li>
                        I am not classified as a Politically Exposed Person (PEP).
                      </li>
                      <li>
                        I have reviewed and understand the Master Agreement, Terms
                        of Use, and Privacy Policy. By proceeding with the
                        registration, I agree to adhere to and accept the terms 
                        outlined therein.
                      </li>
                      <li>
                        I grant Dequitec permission to use my PAN for identity verification and to retrieve my details from the GSTIN system for verification purposes.
                      </li>
                      <li>
                        Additionally, by providing the above information, I authorize NDNC registration and consent to Dequitec or its authorized representatives contacting me via SMS, call, email, or WhatsApp for assistance, without causing me any inconvenience.
                      </li>
                    </ul>
                  </Typography>
                }
              />
            </Box>
          )}

        {/* Submit */}
        <Box sx={{ mt: 4 }}>
          <Button
            variant="contained"
            fullWidth
            sx={{
              backgroundColor: "#003A91",
              "&:hover": { backgroundColor: "#002570" },
              height: "40px",
              textTransform: "none",
            }}
            disabled={!isFormValid()}
            onClick={handleSubmit}
          >
            Submit
          </Button>
        </Box>

        {/* Support Info */}
        <Typography
          sx={{ textAlign: "center", mt: 3, fontSize: "0.8rem", color: "#555" }}
        >
          Not sure about your role? Contact our support team for assistance.
        </Typography>
        <Typography
          sx={{ textAlign: "center", mt: 1, fontSize: "0.8rem", color: "#555" }}
        >
          Support Email:{" "}
          <Link
            href="mailto:contact@dequitec.com"
            sx={{ color: "#003A91", fontWeight: "bold" }}
          >
            contact@dequitec.com
          </Link>
        </Typography>
      </Grid>
    </Grid>
  );
};

export default BorrowerContent;
